/* eslint react-hooks/exhaustive-deps: "warn" */

import { type CSSProperties, useEffect, useState } from 'react';

import cn from 'classnames';

import styles from './ApiStatusRibbon.module.scss';

import type { ApiStatusItem, ApiStatusRibbonProps } from './ApiStatusRibbon.types';

const getTone = (item: ApiStatusItem): 'success' | 'warn' | 'danger' => {
  if (item.ok) {
    return 'success';
  }

  return item.code.startsWith('4') ? 'warn' : 'danger';
};

export const ApiStatusRibbon = ({ items, note, className }: ApiStatusRibbonProps) => {
  const [flashIndex, setFlashIndex] = useState<number | null>(null);

  useEffect(() => {
    if (items.length === 0) {
      return undefined;
    }

    let scheduleTimer: ReturnType<typeof setTimeout>;
    let clearTimer: ReturnType<typeof setTimeout>;
    let active = true;

    const schedule = () => {
      const delay = 1200 + Math.random() * 2400;

      scheduleTimer = setTimeout(() => {
        if (!active) {
          return;
        }

        setFlashIndex(Math.floor(Math.random() * items.length));
        clearTimer = setTimeout(() => {
          if (active) setFlashIndex(null);
        }, 650);

        schedule();
      }, delay);
    };

    schedule();

    return () => {
      active = false;
      clearTimeout(scheduleTimer);
      clearTimeout(clearTimer);
    };
  }, [items.length]);

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.inner}>
        <span className={styles.prompt} aria-hidden="true">
          $
        </span>

        <div className={styles.items} style={{ '--cols': items.length } as CSSProperties}>
          {items.map((item, index) => {
            const tone = getTone(item);

            return (
              <span
                key={item.code}
                className={cn(styles.item, styles[`item_${tone}`], index === flashIndex && styles.itemFlash)}
              >
                {item.ok && <span className={styles.pulse} aria-hidden="true" />}
                <b className={styles.code}>{item.code}</b>
                <span className={styles.label}>{item.label}</span>
              </span>
            );
          })}
        </div>

        <span className={styles.tail}>
          {note && <span className={styles.note}>{note}</span>}
          <span className={styles.cursor} aria-hidden="true" />
        </span>
      </div>
    </div>
  );
};
